import type {
  AiLog,
  ProjectState,
  WorkspaceCheck,
  WorkspaceFile,
  WorkspaceReport,
} from "./types";

const SKIPPED_DIRECTORIES = new Set([
  ".git",
  ".venv",
  "node_modules",
  ".next",
  ".vinext",
  "dist",
  "coverage",
  "__pycache__",
]);

const TEXT_EXTENSIONS = new Set([
  "md",
  "txt",
  "tex",
  "bib",
  "json",
  "jsonl",
  "csv",
  "yaml",
  "yml",
  "toml",
]);

export async function requestWorkspaceDirectory() {
  if (!window.showDirectoryPicker) {
    throw new Error(
      "当前浏览器不支持本地目录绑定。请使用最新版 Chrome 或 Edge。",
    );
  }
  return window.showDirectoryPicker({
    id: "scholarflow-workspace",
    mode: "readwrite",
  });
}

export async function ensureHandlePermission(
  handle: FileSystemDirectoryHandle,
): Promise<boolean> {
  const current = await handle.queryPermission({ mode: "readwrite" });
  if (current === "granted") return true;
  return (await handle.requestPermission({ mode: "readwrite" })) === "granted";
}

async function collectFiles(
  directory: FileSystemDirectoryHandle,
  prefix: string,
  depth: number,
  output: WorkspaceFile[],
  maxFiles: number,
) {
  if (depth > 6 || output.length >= maxFiles) return;
  for await (const [name, handle] of directory.entries()) {
    if (output.length >= maxFiles) break;
    const path = prefix ? `${prefix}/${name}` : name;
    if (handle.kind === "directory") {
      if (!SKIPPED_DIRECTORIES.has(name)) {
        await collectFiles(handle, path, depth + 1, output, maxFiles);
      }
      continue;
    }
    const file = await handle.getFile();
    output.push({
      path,
      name,
      extension: name.includes(".") ? name.split(".").pop()!.toLowerCase() : "",
      size: file.size,
      modifiedAt: file.lastModified,
    });
  }
}

async function getFileByPath(
  root: FileSystemDirectoryHandle,
  path: string,
): Promise<FileSystemFileHandle> {
  const parts = path.split("/").filter(Boolean);
  let directory = root;
  for (const part of parts.slice(0, -1)) {
    directory = await directory.getDirectoryHandle(part);
  }
  return directory.getFileHandle(parts.at(-1)!);
}

async function readSnippet(
  root: FileSystemDirectoryHandle,
  file: WorkspaceFile,
) {
  try {
    const handle = await getFileByPath(root, file.path);
    const blob = await handle.getFile();
    return (await blob.text()).slice(0, 6000);
  } catch {
    return "";
  }
}

function buildChecks(files: WorkspaceFile[]): WorkspaceCheck[] {
  const paths = files.map((file) => file.path.toLowerCase());
  const matching = (patterns: RegExp[]) =>
    files
      .filter((file) => patterns.some((pattern) => pattern.test(file.path)))
      .map((file) => file.path)
      .slice(0, 12);
  const check = (
    id: string,
    label: string,
    full: RegExp[],
    partial: RegExp[],
    detail: string,
  ): WorkspaceCheck => {
    const completeMatches = matching(full);
    const partialMatches = matching(partial);
    return {
      id,
      label,
      status: completeMatches.length
        ? "complete"
        : partialMatches.length
          ? "partial"
          : "missing",
      detail,
      matchedFiles: completeMatches.length ? completeMatches : partialMatches,
    };
  };

  const checks = [
    check(
      "venue",
      "Venue 要求",
      [/01_venue\/requirements\.(md|json)$/i],
      [/venue|call.for.paper|requirements/i],
      "保留原始链接、页面抓取时间、明确要求和未知项。",
    ),
    check(
      "direction",
      "选题决策",
      [/02_direction\/decision_record\.md$/i],
      [/direction|research.design|proposal/i],
      "记录候选方向、选择理由、风险与转向条件。",
    ),
    check(
      "experiment_code",
      "实验实现",
      [/03_experiments\/code\/.+\.(py|js|ts|ipynb)$/i],
      [/\.(py|ipynb)$/i],
      "代码、配置、依赖和 seed 应能定位。",
    ),
    check(
      "raw_results",
      "原始实验记录",
      [/03_experiments\/results\/raw\/.+\.(jsonl|json|csv)$/i],
      [/results_raw|raw.*\.(jsonl|json|csv)$/i],
      "原始记录与后处理结果分离，保留失败运行。",
    ),
    check(
      "processed_results",
      "冻结结果与绘图输入",
      [/03_experiments\/results\/processed\/.+\.(json|csv)$/i],
      [/processed|summary|metrics/i],
      "正文表格和图应从冻结的中间结果生成。",
    ),
    check(
      "paper",
      "论文正文",
      [/04_paper\/main\.tex$/i, /04_paper\/paper\.pdf$/i],
      [/main\.tex$/i, /\.pdf$/i],
      "正文、参考文献、最终图表和可编译源码应一致。",
    ),
    check(
      "ledger",
      "主张—证据台账",
      [/04_paper\/claim_evidence_ledger\.csv$/i],
      [/claim.*evidence|evidence.*ledger/i],
      "每个核心主张映射到表、图、实验或引用。",
    ),
    check(
      "review",
      "评审与修改记录",
      [/05_review\/action_tracker\.md$/i],
      [/review|rebuttal|action_tracker/i],
      "保留共同问题、决策、修改位置和未采纳原因。",
    ),
    check(
      "submission",
      "投稿交付",
      [/06_submission\/openreview_fields\.md$/i, /06_submission\/.+\.pdf$/i],
      [/submission|openreview|checklist/i],
      "核对 PDF、表单字段、匿名/单盲规则、代码包和哈希。",
    ),
    check(
      "audit_logs",
      "AI 审计日志",
      [/00_admin\/ai_logs\/conversations\.jsonl$/i],
      [/ai_logs|conversation.*jsonl/i],
      "模型请求、响应、用途、模型版本和压缩事件均写入固定位置。",
    ),
  ];

  const weights: Record<WorkspaceCheck["status"], number> = {
    complete: 1,
    partial: 0.5,
    missing: 0,
  };
  void paths;
  (checks as Array<WorkspaceCheck & { _score?: number }>).forEach(
    (item) => (item._score = weights[item.status]),
  );
  return checks;
}

export async function scanWorkspace(
  root: FileSystemDirectoryHandle,
): Promise<WorkspaceReport> {
  const files: WorkspaceFile[] = [];
  await collectFiles(root, "", 0, files, 800);
  const checks = buildChecks(files);
  const score = Math.round(
    (checks.reduce(
      (sum, item) =>
        sum + (item.status === "complete" ? 1 : item.status === "partial" ? 0.5 : 0),
      0,
    ) /
      checks.length) *
      100,
  );
  const relevant = files
    .filter(
      (file) =>
        TEXT_EXTENSIONS.has(file.extension) &&
        file.size < 250_000 &&
        /(requirements|decision|contract|summary|metrics|main\.tex|readme|checklist|ledger)/i.test(
          file.path,
        ),
    )
    .slice(0, 10);
  const snippets = [];
  for (const file of relevant) {
    const text = await readSnippet(root, file);
    if (text) snippets.push({ path: file.path, text });
  }
  return {
    scannedAt: new Date().toISOString(),
    rootName: root.name,
    fileCount: files.length,
    files,
    checks,
    score,
    snippets,
  };
}

async function ensureDirectory(
  root: FileSystemDirectoryHandle,
  path: string,
): Promise<FileSystemDirectoryHandle> {
  let directory = root;
  for (const part of path.split("/").filter(Boolean)) {
    directory = await directory.getDirectoryHandle(part, { create: true });
  }
  return directory;
}

async function writeFile(
  root: FileSystemDirectoryHandle,
  path: string,
  content: string,
  overwrite = false,
): Promise<"created" | "updated" | "skipped"> {
  const parts = path.split("/").filter(Boolean);
  const directory = await ensureDirectory(root, parts.slice(0, -1).join("/"));
  let existed = true;
  try {
    await directory.getFileHandle(parts.at(-1)!);
  } catch {
    existed = false;
  }
  if (existed && !overwrite) return "skipped";
  const handle = await directory.getFileHandle(parts.at(-1)!, { create: true });
  const writer = await handle.createWritable();
  await writer.write(content);
  await writer.close();
  return existed ? "updated" : "created";
}

async function appendLine(
  root: FileSystemDirectoryHandle,
  path: string,
  line: string,
) {
  let existing = "";
  try {
    const handle = await getFileByPath(root, path);
    existing = await (await handle.getFile()).text();
  } catch {
    // The first append creates the file.
  }
  await writeFile(root, path, `${existing}${line}\n`, true);
}

export async function persistAiLogToWorkspace(
  root: FileSystemDirectoryHandle,
  log: AiLog,
) {
  const safeLog = { ...log, apiKey: undefined };
  await appendLine(
    root,
    "00_admin/ai_logs/conversations.jsonl",
    JSON.stringify(safeLog),
  );
}

export async function persistCapsuleToWorkspace(
  root: FileSystemDirectoryHandle,
  capsule: ProjectState["memory"]["capsules"][number],
) {
  await appendLine(
    root,
    "00_admin/memory/context_capsules.jsonl",
    JSON.stringify(capsule),
  );
}

export async function syncAuditRecordsToWorkspace(
  root: FileSystemDirectoryHandle,
  logs: AiLog[],
  capsules: ProjectState["memory"]["capsules"],
) {
  const syncJsonl = async (
    path: string,
    records: Array<{ id: string }>,
  ) => {
    let existing = "";
    try {
      const handle = await getFileByPath(root, path);
      existing = await (await handle.getFile()).text();
    } catch {
      // The first sync creates the file.
    }
    const knownIds = new Set(
      existing
        .split(/\r?\n/)
        .filter(Boolean)
        .flatMap((line) => {
          try {
            const parsed = JSON.parse(line) as { id?: string };
            return parsed.id ? [parsed.id] : [];
          } catch {
            return [];
          }
        }),
    );
    const missing = records.filter((record) => !knownIds.has(record.id));
    if (!missing.length) return 0;
    const suffix = missing.map((record) => JSON.stringify(record)).join("\n");
    await writeFile(
      root,
      path,
      `${existing}${existing && !existing.endsWith("\n") ? "\n" : ""}${suffix}\n`,
      true,
    );
    return missing.length;
  };
  const logCount = await syncJsonl(
    "00_admin/ai_logs/conversations.jsonl",
    logs.map((log) => ({ ...log, apiKey: undefined })),
  );
  const capsuleCount = await syncJsonl(
    "00_admin/memory/context_capsules.jsonl",
    capsules,
  );
  return { logCount, capsuleCount };
}

export async function persistPromptToWorkspace(
  root: FileSystemDirectoryHandle,
  mode: string,
  prompt: string,
) {
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");
  return writeFile(
    root,
    `prompts/generated/${stamp}_${mode}.md`,
    `# ScholarFlow generated prompt\n\n${prompt}\n`,
  );
}

export async function scaffoldWorkspace(
  root: FileSystemDirectoryHandle,
  project: ProjectState,
  prompt: string,
) {
  const projectBrief = `# ${project.projectName}

## Research idea

${project.idea || "_To be completed_"}

## Bound venue

${project.venueUrls || "_To be completed_"}

## Operating rule

Every claim must be traceable to a source, experiment record, table, or figure.
Failed runs remain in the audit log. Main and post-hoc analyses stay separate.
`;
  const venueRequirements = `# Venue requirements

## Source URLs

${project.venueUrls || "_Add one URL per line._"}

## Verified requirements

${project.venueBrief?.facts.map((fact) => `- **${fact.label}:** ${fact.value} (${fact.evidenceUrl})`).join("\n") || "_Run venue analysis in ScholarFlow._"}

## Unknowns

${project.venueBrief?.unknowns.map((item) => `- ${item}`).join("\n") || "_No unknowns recorded yet._"}
`;
  const direction = project.directions.find(
    (item) => item.id === project.selectedDirectionId,
  );
  const decisionRecord = `# Direction decision record

## Selected direction

${direction ? `**${direction.title}** — ${direction.oneLine}` : "_Not selected yet._"}

## Research question

${direction?.researchQuestion || "_To be completed_"}

## Mechanism

${direction?.proposedMechanism || "_To be completed_"}

## Risks and pivot signal

${direction?.risks.map((risk) => `- ${risk}`).join("\n") || "_To be completed_"}

${direction?.pivotSignal ? `Pivot when: ${direction.pivotSignal}` : ""}
`;
  const experimentContract = `# Experiment contract

- **Task:** ${project.experiment.taskDefinition || "_TBD_"}
- **Datasets:** ${project.experiment.datasets || "_TBD_"}
- **Primary metric:** ${project.experiment.primaryMetric || "_TBD_"}
- **Secondary metrics:** ${project.experiment.secondaryMetrics || "_TBD_"}
- **Baselines:** ${project.experiment.baselines || "_TBD_"}
- **Main comparison:** ${project.experiment.mainComparison || "_TBD_"}
- **Ablations:** ${project.experiment.ablations || "_TBD_"}
- **Robustness:** ${project.experiment.robustness || "_TBD_"}
- **Seeds:** ${project.experiment.seeds}
- **Compute budget:** ${project.experiment.computeBudget || "_TBD_"}
- **Stop criteria:** ${project.experiment.stopCriteria}
- **Pivot criteria:** ${project.experiment.pivotCriteria}

Results may only be promoted into the paper from frozen, script-generated
processed records. Do not edit reported values by hand.
`;
  const claimLedger = `claim_id,claim,claim_type,evidence_file,evidence_location,status,notes
C1,,core,,,planned,
`;
  const figurePlan = `# Figure plan

Preferred Codex skill: \`${project.paper.figureSkill}\`

${project.paper.figures
  .map(
    (figure) => `## ${figure.role}

- Message: ${figure.message}
- Evidence boundary: ${figure.evidence}
- Deliverables: ${figure.format}
`,
  )
  .join("\n")}

Every final figure requires an editable source, PDF export, raster preview,
caption check, and visual inspection record.
`;
  const actionTracker = `# Review action tracker

| ID | Reviewer concern | Shared by | Severity | Decision | Change location | Verification |
|---|---|---:|---:|---|---|---|
`;
  const submissionChecklist = `# Submission checklist

- [ ] Venue and track requirements rechecked against source URLs
- [ ] Author/anonymity mode matches the current submission stage
- [ ] Page limits, references, appendix, ethics and AI disclosure checked
- [ ] PDF visually inspected page by page
- [ ] All links open without privileged access
- [ ] Artifact ZIP rebuilt from the current unpacked source
- [ ] OpenReview fields match the PDF metadata
- [ ] SHA-256 hashes recorded
`;
  const files: Record<string, string> = {
    "00_admin/project_brief.md": projectBrief,
    "00_admin/status.md": "# Project status\n\nCurrent stage: venue\n",
    "00_admin/ai_logs/conversations.jsonl": "",
    "00_admin/memory/context_capsules.jsonl": "",
    "00_admin/decisions/decision_ledger.md":
      "# Decision ledger\n\nRecord decisions with date, rationale, evidence, and superseded alternatives.\n",
    "01_venue/requirements.md": venueRequirements,
    "02_direction/decision_record.md": decisionRecord,
    "03_experiments/experiment_contract.md": experimentContract,
    "03_experiments/results/raw/README.md":
      "# Raw results\n\nImmutable provider responses and run-level records. Never hand-edit.\n",
    "03_experiments/results/processed/README.md":
      "# Processed results\n\nScript-generated summaries used by tables and figures.\n",
    "04_paper/claim_evidence_ledger.csv": claimLedger,
    "04_paper/figures/figure_plan.md": figurePlan,
    "05_review/action_tracker.md": actionTracker,
    "06_submission/checklist.md": submissionChecklist,
    "prompts/master_prompt.md": `# Current master prompt\n\n${prompt}\n`,
    ".scholarflow/project.json": JSON.stringify(project, null, 2),
  };
  const results = [];
  for (const [path, content] of Object.entries(files)) {
    results.push({ path, result: await writeFile(root, path, content) });
  }
  const emptyDirectories = [
    "03_experiments/code",
    "03_experiments/configs",
    "03_experiments/runs",
    "03_experiments/results/figures",
    "03_experiments/logs",
    "04_paper/sections",
    "04_paper/tables",
    "04_paper/figures/briefs",
    "04_paper/figures/drafts",
    "04_paper/figures/editable",
    "04_paper/figures/final",
    "04_paper/figures/qa",
    "05_review/rounds",
    "06_submission/paper_source",
    "06_submission/artifact",
    "prompts/generated",
  ];
  for (const path of emptyDirectories) await ensureDirectory(root, path);
  return results;
}
